<template>
  <div class="flex flex-col min-w-0 max-w-full">
    <!-- GF-T6: dar ekranda (375 px) taşmasın — numara kutusu min-w-0 ile küçülebilir. Ülke kodu w-24: etiketin sağ boşluğu ve ok alanı daraltılır, en uzun kod (+994) sığar. -->
    <div class="flex items-stretch w-full min-w-0 focus-within:ring-1 transition-shadow rounded-md">
      <Select
        v-model="dialCode"
        :options="ULKE_KODLARI"
        optionLabel="ad"
        optionValue="dial"
        filter
        :disabled="readonly"
        :class="['w-24 shrink-0', selectRadiusClass]"
        :pt="{ label: { class: '!pr-0' }, dropdown: { class: '!w-7' } }"
        @change="ulkeDegisti"
      >
        <template #value="slotProps">
          <span v-if="slotProps.value">{{ slotProps.value }}</span>
          <span v-else class="text-surface-400">Kod</span>
        </template>
        <template #option="slotProps">
          {{ slotProps.option.ad }} ({{ slotProps.option.dial }})
        </template>
      </Select>
      <InputText
        v-model="localNumber"
        :id="inputId || undefined"
        :readonly="readonly"
        :autocomplete="autocomplete || undefined"
        inputmode="tel"
        :invalid="invalid"
        :maxlength="aktifUlke ? aktifUlke.uzunluk : 14"
        :class="['flex-1 min-w-0 w-0 !border-l-0', inputRadiusClass, inputClass]"
        :placeholder="placeholder"
        @input="temizle"
      />
    </div>
    <small v-if="!invalid && !readonly && formatIpucu" class="mt-1 text-gray-500 dark:text-gray-400">{{ formatIpucu }}</small>
  </div>
</template>

<script>
import { ULKE_KODLARI, ulkeBul } from "@/utils/ulkeKodlari";
import { telefonE164Yap } from "@/utils/validation";

// Tailwind sınıfları derleme zamanında (JIT) kaynak dosyada TAM METİN olarak görünmeli —
// "rounded-" + değişken parçasını runtime'da birleştirmek Tailwind'in taramasında görünmez ve
// production build'de sınıf üretilmez. Bu yüzden desteklenen her radius değeri için sol/sağ
// kombinasyonu burada eksiksiz, literal string olarak tanımlı.
const RADIUS_ESLESME = {
  "rounded-none": { sol: "!rounded-l-none !rounded-r-none", sag: "!rounded-r-none !rounded-l-none" },
  "rounded-sm": { sol: "!rounded-l-sm !rounded-r-none", sag: "!rounded-r-sm !rounded-l-none" },
  "rounded-md": { sol: "!rounded-l-md !rounded-r-none", sag: "!rounded-r-md !rounded-l-none" },
  "rounded-lg": { sol: "!rounded-l-lg !rounded-r-none", sag: "!rounded-r-lg !rounded-l-none" },
  "rounded-xl": { sol: "!rounded-l-xl !rounded-r-none", sag: "!rounded-r-xl !rounded-l-none" },
  "rounded-2xl": { sol: "!rounded-l-2xl !rounded-r-none", sag: "!rounded-r-2xl !rounded-l-none" },
  "rounded-full": { sol: "!rounded-l-full !rounded-r-none", sag: "!rounded-r-full !rounded-l-none" },
};

export default {
  name: "TelefonGirisi",
  props: {
    modelValue: { type: String, default: "" },
    invalid: { type: Boolean, default: false },
    placeholder: { type: String, default: "5xx xxx xx xx" },
    inputClass: { type: String, default: "" },
    // Ülke kodu + numara tek parça bir kutu gibi görünür (aradaki kenarlık kaldırılır);
    // dış köşelerin yuvarlaklığı bu tek prop'tan türetilir, sayfanın temasına göre override
    // edilebilir (ör. public sayfalarda "rounded-xl").
    radius: { type: String, default: "rounded-md" },
    defaultDialCode: { type: String, default: "+90" },
    // TLF1: salt-okunur gösterim (ülke seçimi kapalı, ipucu gizli), numara kutusunun id'si
    // (<label for> bağı) ve tarayıcı önerisi (ör. "tel-national").
    readonly: { type: Boolean, default: false },
    inputId: { type: String, default: "" },
    autocomplete: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      ULKE_KODLARI,
      dialCode: this.defaultDialCode,
      localNumber: "",
    };
  },
  computed: {
    aktifUlke() {
      return ulkeBul(this.dialCode);
    },
    formatIpucu() {
      return this.aktifUlke ? `Beklenen: ${this.aktifUlke.dial} + ${this.aktifUlke.uzunluk} haneli numara` : "";
    },
    // Tek prop'tan iki taraf da türetildiği için asla çakışan yuvarlaklık sınıfı oluşmaz.
    // Bilinmeyen bir değer verilirse varsayılan "rounded-md" eşlemesine düşer.
    aktifRadius() {
      return RADIUS_ESLESME[this.radius] || RADIUS_ESLESME["rounded-md"];
    },
    selectRadiusClass() {
      return this.aktifRadius.sol;
    },
    inputRadiusClass() {
      return this.aktifRadius.sag;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(v) {
        this.disaridanYukle(v);
      },
    },
  },
  methods: {
    // Dışarıdan gelen değeri (E.164 ya da eski/ham format) ülke kodu + yerel numaraya ayırır.
    disaridanYukle(v) {
      // TLF1: eski TR biçimi (05…, 905…) önce E.164'e çevrilir — "+9005…" görünmesin.
      const deger = telefonE164Yap(v ? String(v) : "", this.defaultDialCode);
      // Zaten kendi ürettiğimiz değeri tekrar ayrıştırıp gereksiz yere state'i bozma.
      if (deger === this.birlesikDeger()) return;

      if (deger.startsWith("+")) {
        const eslesen = this.ULKE_KODLARI
          .filter((u) => deger.startsWith(u.dial))
          .sort((a, b) => b.dial.length - a.dial.length)[0];
        if (eslesen) {
          this.dialCode = eslesen.dial;
          this.localNumber = deger.slice(eslesen.dial.length).replace(/[^\d]/g, "");
          return;
        }
      }
      // Eski/ham veri (boşluklu, "0" ile başlayan, tireli vb.) — best-effort: rakamları al,
      // TR varsayılanında bırak; kullanıcı gerekirse elle düzeltir, kaydedince E.164 çıkar.
      this.dialCode = this.defaultDialCode;
      this.localNumber = deger.replace(/[^\d]/g, "");
    },
    // Ülke koduna göre sabit uzunluk mask'i — fazla rakam kabul edilmez.
    maxUzunluk() {
      return this.aktifUlke ? this.aktifUlke.uzunluk : 14;
    },
    temizle() {
      this.localNumber = this.localNumber.replace(/[^\d]/g, "").slice(0, this.maxUzunluk());
      this.yayinla();
    },
    // Ülke değişince mevcut numara yeni ülkenin uzunluğuna göre kırpılır.
    ulkeDegisti() {
      this.localNumber = this.localNumber.slice(0, this.maxUzunluk());
      this.yayinla();
    },
    birlesikDeger() {
      return this.localNumber ? this.dialCode + this.localNumber : "";
    },
    yayinla() {
      this.$emit("update:modelValue", this.birlesikDeger());
    },
  },
};
</script>
